import { useRef, useEffect } from "react";

/**
 * Six-box OTP-style numeric input. Each digit gets its own focus-able cell:
 * - typing a digit auto-advances to the next box
 * - backspace on an empty box jumps back and clears the previous one
 * - ArrowLeft / ArrowRight nudge focus without changing the value
 * - pasting a 6-digit code fills every box at once
 *
 * `autoComplete="one-time-code"` tells the browser this is an SMS / email
 * verification code so it offers the right autofill (and stops trying to
 * inject the saved username).
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  autoFocus = true,
  disabled = false,
}: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setAt = (idx: number, char: string) => {
    const arr = value.padEnd(length, " ").split("");
    arr[idx] = char || " ";
    const next = arr.join("").replace(/ /g, "").slice(0, length);
    onChange(next);
  };

  const handleChange = (idx: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    // Browser autofill (iOS / Chrome SMS autofill) sometimes shoves the whole
    // code into a single cell without firing a paste event. Detect that case
    // and spread the digits across the boxes instead of swallowing all but one.
    if (digits.length > 1) {
      const next = digits.slice(0, length);
      onChange(next);
      refs.current[Math.min(next.length, length - 1)]?.focus();
      return;
    }
    setAt(idx, digits);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace") {
      if (!value[idx] && idx > 0) {
        // Empty cell + backspace → clear the previous one and move back.
        e.preventDefault();
        setAt(idx - 1, "");
        refs.current[idx - 1]?.focus();
      } else if (value[idx]) {
        e.preventDefault();
        setAt(idx, "");
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className="flex justify-center gap-2" role="group" aria-label="Doğrulama kodu">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          autoComplete="one-time-code"
          disabled={disabled}
          aria-label={`Hane ${i + 1}`}
          className="size-12 rounded-lg border bg-background text-center text-xl font-bold tabular-nums shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
        />
      ))}
    </div>
  );
}
